import { uniqBy } from "lodash-es";
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, reactive, toRefs, watch } from "vue";

import { DictionariesHelper, DictionaryMethodHelper, SearchFilterHelper } from "@/helpers";
import type { Dictionary, SearchDirection, SearchFilter, SearchLanguage } from "@/types";

import { useDictionariesStore } from ".";

interface State {
  filter: SearchFilter;
}

export const useSearchFilterStore = defineStore("searchFilter", () => {
  const state = reactive<State>({
    filter: { direction: SearchFilterHelper.DEFAULT_DIRECTION }
  });

  const dictionariesStore = useDictionariesStore();

  const isSearchLanguage = (language: SearchLanguage | undefined): language is SearchLanguage => language !== undefined;

  const uniqueSortedLanguages = (languages: SearchLanguage[]): SearchLanguage[] =>
    uniqBy(languages, "code").sort((a, b) => a.name.localeCompare(b.name));

  const availableSourceLanguages = computed<SearchLanguage[]>(() => {
    const languages = dictionariesStore.methods
      .map((method) => DictionaryMethodHelper.getSourceLanguage(method))
      .filter(isSearchLanguage);

    return uniqueSortedLanguages(languages);
  });

  const availableTargetLanguages = computed<SearchLanguage[]>(() => {
    if (!state.filter.sourceLanguage) {
      return [];
    }

    const languages = dictionariesStore.methods
      .filter((method) => method.sourceLanguageCode === state.filter.sourceLanguage?.code)
      .map((method) => DictionaryMethodHelper.getTargetLanguage(method))
      .filter(isSearchLanguage);

    return uniqueSortedLanguages(languages);
  });

  const availableSearchDirections = computed<SearchDirection[]>(() => {
    if (!state.filter.sourceLanguage) {
      return ["two-way", "one-way"];
    }

    const directions: SearchDirection[] = ["one-way"];

    const hasTwoWay = dictionariesInSourceLanguage.value.some((dictionary) =>
      DictionariesHelper.isBiDirectional(dictionary)
    );

    if (hasTwoWay) {
      directions.unshift("two-way");
    }

    return directions;
  });

  const dictionariesInSourceLanguage = computed<Dictionary[]>(() => {
    if (!state.filter.sourceLanguage) {
      return dictionariesStore.dictionaries;
    }

    return dictionariesStore.dictionaries.filter((dictionary) =>
      Object.values(dictionary.methods).some(
        (method) => DictionaryMethodHelper.getSourceLanguage(method)?.code === state.filter.sourceLanguage?.code
      )
    );
  });

  watch(availableTargetLanguages, (languages) => {
    if (!state.filter.targetLanguage) {
      return;
    }

    if (!languages.length) {
      return;
    }

    // Restore target language name if it is not set, e.g. when restored from URL serialization
    if (!state.filter.targetLanguage.name) {
      state.filter.targetLanguage = languages.find((language) => language.code === state.filter.targetLanguage?.code);
    }
  });

  watch(availableSearchDirections, (directions) => {
    if (!directions.includes(state.filter.direction)) {
      Object.assign(state.filter, { ...state.filter, direction: availableSearchDirections.value[0] });
    }
  });

  return {
    ...toRefs(state),
    availableSourceLanguages,
    availableTargetLanguages,
    availableSearchDirections,
    dictionariesInSourceLanguage
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSearchFilterStore, import.meta.hot));
}
